/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ClipboardCheck, ChevronRight, AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * Tracking & Observations card for the Care Dashboard.
 * Source of truth: GET /api/tracking/dashboard-signal/{subscriber_id}
 *
 * Read-only signal panel. Surfaces:
 *   • latest submission date + form_type
 *   • 7d wellbeing average + tiny trend
 *   • open flag count (links to admin Tracking OS for care team)
 *   • admin review status of latest submission
 */
export default function TrackingObservationsCard({ subscriberId, viewerRole }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isCareTeam = ['admin', 'counselor', 'health_coach', 'doctor'].includes(viewerRole);

  useEffect(() => {
    if (!subscriberId) return;
    let cancelled = false;
    axios.get(`${API}/tracking/dashboard-signal/${subscriberId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [subscriberId]);

  if (loading) {
    return (
      <Card className="bg-[#131B2B] border-white/10" data-testid="tracking-observations-card-loading">
        <CardContent className="p-5 text-sm text-white/40">Loading tracking signals…</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { latest, open_flags_count, trend_7d, average_7d, admin_review_status, pattern_hints } = data;
  const hasData = !!latest;
  const hints = (pattern_hints || []).slice(0, 2);

  const trendDirection = (() => {
    if (!trend_7d || trend_7d.length < 2) return 'flat';
    const first = trend_7d[0].wellbeing_index;
    const last = trend_7d[trend_7d.length - 1].wellbeing_index;
    if (last - first > 0.3) return 'up';
    if (first - last > 0.3) return 'down';
    return 'flat';
  })();
  const TrendIcon = trendDirection === 'up' ? TrendingUp : trendDirection === 'down' ? TrendingDown : Minus;
  const trendTone = trendDirection === 'up'
    ? 'text-emerald-400'
    : trendDirection === 'down'
      ? 'text-rose-400'
      : 'text-white/50';

  return (
    <Card className="bg-[#131B2B] border-white/10" data-testid="tracking-observations-card">
      <CardHeader className="border-b border-white/10 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-cyan-300" />
            Tracking & Observations
          </CardTitle>
          {isCareTeam && (
            <Link
              to={`/admin/tracking-os`}
              className="text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1"
              data-testid="tracking-card-open-os"
            >
              Open Tracking OS <ChevronRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {!hasData ? (
          <p className="text-sm text-white/50" data-testid="tracking-card-empty">
            No observations submitted yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="rounded border border-white/10 bg-black/20 p-3">
              <div className="text-[10px] uppercase tracking-wider text-white/50">Latest</div>
              <div className="text-sm text-white mt-1" data-testid="tracking-card-latest-date">{latest.observation_date}</div>
              <div className="text-[11px] text-white/40 mt-0.5">{latest.form_type || 'daily'}</div>
            </div>
            <div className="rounded border border-white/10 bg-black/20 p-3">
              <div className="text-[10px] uppercase tracking-wider text-white/50">7d wellbeing</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-2xl font-medium text-white tabular-nums" data-testid="tracking-card-avg-7d">
                  {average_7d ?? '—'}
                </span>
                <TrendIcon className={`w-4 h-4 ${trendTone}`} />
              </div>
              <div className="text-[11px] text-white/40 mt-0.5">{trend_7d?.length || 0} day{(trend_7d?.length || 0) === 1 ? '' : 's'} of data</div>
            </div>
            <div className={`rounded border p-3 ${
              open_flags_count > 0
                ? 'border-rose-500/30 bg-rose-500/10'
                : 'border-emerald-500/20 bg-emerald-500/5'
            }`}>
              <div className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Open flags
              </div>
              <div className={`text-2xl font-medium tabular-nums mt-1 ${open_flags_count > 0 ? 'text-rose-300' : 'text-emerald-300'}`}
                data-testid="tracking-card-open-flags">
                {open_flags_count ?? 0}
              </div>
            </div>
            <div className="rounded border border-white/10 bg-black/20 p-3">
              <div className="text-[10px] uppercase tracking-wider text-white/50">Review status</div>
              <Badge className={`mt-1 text-xs ${
                admin_review_status === 'reviewed'       ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
                admin_review_status === 'action_created' ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' :
                                                            'bg-amber-500/15 text-amber-300 border-amber-500/30'
              }`} data-testid="tracking-card-review-status">
                {admin_review_status || 'new'}
              </Badge>
            </div>
          </div>
        )}

        {/* Pattern hints (deterministic, no LLM) */}
        {hasData && hints.length > 0 && (
          <div className="mt-4" data-testid="tracking-card-pattern-hints">
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Pattern hints</div>
            <ul className="space-y-1.5">
              {hints.map((h) => (
                <li key={h.id}
                  data-testid={`pattern-hint-${h.id}`}
                  className={`flex items-start gap-2 text-sm rounded border px-3 py-2 ${
                    h.severity === 'concern' ? 'border-rose-500/25 bg-rose-500/5 text-rose-200' :
                    h.severity === 'watch'   ? 'border-amber-500/25 bg-amber-500/5 text-amber-200' :
                                                'border-cyan-500/20 bg-cyan-500/5 text-cyan-200'
                  }`}>
                  <span className="text-[10px] uppercase tracking-wider opacity-70 shrink-0 mt-0.5">{h.severity}</span>
                  <span className="leading-relaxed">{h.text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
